import {
    Component,
    ViewEncapsulation,
    OnInit,
    inject,
    Input,
    Output,
    EventEmitter,
} from "@angular/core";
import { CerrixInputValidationRegistryService } from "@app/shared/directives/cerrix-input-fields/cerrix-input-validation-registry.service";
import { provideTranslocoScope, TranslocoPipe, TranslocoService } from "@jsverse/transloco";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { formatIsoDate } from "@methods/CommonMethods";
import { CerrixInstructionsIconComponent } from "@app/shared/cerrix-instructions-icon/cerrix-instructions-icon.component";
import { AssetManagementModel } from "@models/assetmanagement/assetmanagementmodel";
import { UpdateMetaDataModel } from "@models/assetmanagement/updatemetadatamodel";
import { CerrixButtonComponent } from "@app/shared/atoms/cerrix-button/cerrix-button.component";
import { finalize, Observable, firstValueFrom } from "rxjs";
import { Pages } from "@constants/pages/Pages";
import { TabService } from "@services/tabs/TabService";
import { ToastrService } from "ngx-toastr";
import {
    AssetManagementService,
    AssetProjection,
} from "@app/assetmanagement/services/assetmanagement-data.service";
import { CerrixConfirmDialogComponent } from "@app/shared/organisms/cerrix-confirm-dialog/cerrix-confirm-dialog.component";
import { CerrixDialogService } from "@app/shared/organisms/cerrix-dialog/cerrix-dialog-service.component";
import { CerrixDatepickerGroupComponent } from "@app/shared/molecules/cerrix-datepicker-group/cerrix-datepicker-group.component";
import { LifecycleModel } from "@models/assetmanagement/lifecyclemodel";

@Component({
    selector: "app-edit-asset-lifecycle-information",
    templateUrl: "./edit-asset-lifecycle-information.component.html",
    styleUrls: ["./edit-asset-lifecycle-information.component.scss"],
    imports: [
        TranslocoPipe,
        CerrixInstructionsIconComponent,
        CerrixDatepickerGroupComponent,
        CerrixButtonComponent,
        CerrixConfirmDialogComponent,
    ],
    providers: [CerrixInputValidationRegistryService, provideTranslocoScope("assetmanagement")],
    encapsulation: ViewEncapsulation.None,
})
export class EditAssetLifecycleInformationComponent implements OnInit {
    @Input() model: AssetManagementModel;

    @Output() modelChange: EventEmitter<AssetManagementModel> =
        new EventEmitter<AssetManagementModel>();
    @Output() closeEdit: EventEmitter<void> = new EventEmitter<void>();

    protected editModel: UpdateMetaDataModel;

    protected editSaving = false;

    private _ds = inject(AssetManagementService);
    private _validationRegistry = inject(CerrixInputValidationRegistryService);
    private _promptService = inject(CerrixPromptService);
    private _transloco = inject(TranslocoService);
    private _tabService = inject(TabService);
    private _pages = inject(Pages);
    private _toastService = inject(ToastrService);
    private _dialogService = inject(CerrixDialogService);

    public confirmUnsavedChangesDialogName = "lifecycleConfirmUnsavedChangesDialog";

    async ngOnInit() {
        // Ensure the scope JSON is loaded before the synchronous translate() calls below,
        // otherwise they resolve to the raw key while the lazy scope is still loading.
        await firstValueFrom(this._transloco.load(`assetmanagement/${this._transloco.getActiveLang()}`));
        this.revertChanges();
    }

    protected openUnsavedChangesConfirmDialog() {
        if (
            this.model.name !== this.editModel.name ||
            this.model.description !== this.editModel.description
        ) {
            this._dialogService.openDialog(this.confirmUnsavedChangesDialogName);
        } else {
            this.editCancel();
        }
    }

    protected closeUnsavedChangesConfirmDialog() {
        this._dialogService.closeDialog(this.confirmUnsavedChangesDialogName);
    }

    protected editCancel() {
        this.closeUnsavedChangesConfirmDialog();
        this.revertChanges();
        this.closeEdit.emit();
    }

    protected async editSave() {
        this.closeUnsavedChangesConfirmDialog();

        if (!this.validateFields()) {
            return;
        }

        this.editSaving = true;
        const savingPrompt = this._promptService.loader(
            this._transloco.translate("assetmanagement.detail.savingChanges"),
        );
        const storedAsset = await firstValueFrom(
            this.editSaveSection().pipe(
                finalize(() => {
                    savingPrompt.close();
                    this.editSaving = false;
                    this._tabService.refresh(this._pages.AssetManagementOverview);
                }),
            ),
        );

        if (storedAsset?.guid) {
            this.model = Object.assign(new AssetManagementModel(), storedAsset);
            this.modelChange.emit(this.model);
            this.revertChanges();

            this._toastService.success(
                this._transloco.translate("assetmanagement.detail.updateCompleted"),
            );
        }
    }

    private validateFields(): boolean {
        // A field the user never touched has never validated itself, so without this a missing
        // required value would pass unnoticed.
        this._validationRegistry.runValidators();

        if (this._validationRegistry.hasAnyErrors()) {
            this._toastService.warning(
                this._validationRegistry
                    .allMessages()
                    .map(
                        (e) =>
                            this._transloco.translate(
                                "assetmanagement.detail.validationMessagePrefix",
                            ) + e,
                    )
                    .join("\n"),
                this._transloco.translate("assetmanagement.detail.saveFailed"),
            );
            return false;
        }
        return true;
    }

    private editSaveSection(): Observable<AssetProjection> {
        return this._ds.vbetaUpdateAssetLifecycle(this.model.guid, {
            dateInUse: formatIsoDate(this.editModel.lifecycle.dateInUse),
            dateOutOfUse: formatIsoDate(this.editModel.lifecycle.dateOutOfUse),
            version: this.editModel.version,
        });
    }

    private revertChanges(): void {
        this.editModel = Object.assign(new UpdateMetaDataModel(), this.model);
        this.editModel.lifecycle = Object.assign(new LifecycleModel(), this.model.lifecycle);
    }
}
